import { useFormik } from 'formik';
import { AddController } from './interfaces';
import * as Yup from 'yup';
import { useToast } from '@chakra-ui/react';
import { useBikletaPointsService } from '../../../../../services/bikletaPoints/bikletaPointsService';
import { useStore } from '../../../../../utils/useStore';
import { useNavigate } from 'react-router-dom';

const validationSchema = Yup.object().shape({
  title: Yup.string()
    .required('Por favor, ingresa un título')
    .min(2, 'El título no puede contener menos de 2 caractéres')
    .max(40, 'El título no puede contener más de 40 caractéres'),
  description: Yup.string().required('Por favor, ingresa una descripción'),
  status: Yup.string().required('Elija un estado'),
  startDate: Yup.string().required('Ingrese Fecha de inicio'),
  endDate: Yup.string(),
  type: Yup.string().required('Por favor, selecciona un tipo'),
  benefitTag: Yup.string(),
  pointsCost: Yup.number()
    .typeError('Debe colocar un número')
    .min(0, 'El costo en puntos debe ser mayor o igual a 0')
    .nullable(),
  unlimitedQuantity: Yup.boolean(),
  availableQuantity: Yup.number()
    .nullable()
    .when('unlimitedQuantity', {
      is: val => val === false,
      then: Yup.number()
        .typeError('Debe colocar un número')
        .min(1, 'La cantidad debe ser mayor o igual a 1')
        .max(10000, 'La cantidad maxima es de 10.000')
        .required('Debe colocar una cantidad'),
      otherwise: null,
    }),
  coverFile: Yup.mixed().required('Debe subir una imagen del beneficio'),
  bannerFile: Yup.mixed().required(
    'Debe subir una imagen de banner del beneficio'
  ),
  listFile: Yup.mixed().required(
    'Debe subir una imagen de lista del beneficio'
  ),
});

export const useAddController = (): AddController => {
  const toast = useToast();
  const { activePoint } = useStore();
  const bikletaPointsService = useBikletaPointsService();
  const navigation = useNavigate();
  const handleSubmit = async values => {
    try {
      // 1. Subir imágenes primero
      let coverUrl = '';
      let bannerUrl = '';
      let listUrl = '';

      if (values.coverFile) {
        toast({
          title: 'Subiendo imagen de detalle...',
          description: '1 de 3',
          status: 'info',
          duration: 1000,
          position: 'top-right',
        });

        const coverImage = await bikletaPointsService.postImage(
          values.coverFile
        );
        coverUrl = coverImage.url;
      }

      if (values.bannerFile) {
        toast({
          title: 'Subiendo imagen de home...',
          description: '2 de 3',
          status: 'info',
          duration: 1000,
          position: 'top-right',
        });

        const bannerImage = await bikletaPointsService.postImage(
          values.bannerFile
        );
        bannerUrl = bannerImage.url;
      }

      if (values.listFile) {
        toast({
          title: 'Subiendo imagen de lista...',
          description: '3 de 3',
          status: 'info',
          duration: 1000,
          position: 'top-right',
        });

        const listImage = await bikletaPointsService.postImage(
          values.listFile
        );
        listUrl = listImage.url;
      }

      toast({
        title: 'Imágenes subidas correctamente',
        status: 'success',
        duration: 1500,
        position: 'top-right',
      });

      // 2. Preparar el objeto del beneficio
      if (values.unlimitedQuantity === true) {
        delete values.availableQuantity;
      }
      delete values.unlimitedQuantity;
      delete values.coverFile;
      delete values.bannerFile;
      delete values.listFile;

      // Construir el objeto del beneficio
      const temporalBenefit: any = {
        title: values.title,
        description: values.description,
        status: values.status,
        type: values.type,
        startDate: values.startDate + 'T00:00:00',
        coverUrl,
        bannerUrl,
        listUrl,
      };

      // Solo agregar endDate si existe y no está vacío
      if (values.endDate && values.endDate.trim() !== '') {
        temporalBenefit.endDate = values.endDate + 'T23:59:59';
      }

      // Solo agregar benefitTag si existe
      if (values.benefitTag) {
        temporalBenefit.benefitTag = values.benefitTag;
      }

      // Solo agregar availableQuantity si existe
      if (values.availableQuantity) {
        temporalBenefit.availableQuantity = values.availableQuantity;
      }

      // 0 es un costo valido: con `if (values.pointsCost)` nunca se enviaba y el valor
      // anterior quedaba intacto. El input vacio muestra 0, por eso null se trata como 0.
      temporalBenefit.pointsCost = values.pointsCost ?? 0;

      // 3. Crear el beneficio
      await bikletaPointsService.createBenefit(
        activePoint._id,
        temporalBenefit
      );

      toast({
        description: 'Formulario enviado con éxito!',
        title: 'Beneficio creado',
        position: 'top-right',
        status: 'success',
        duration: 2500,
      });

      navigation('/admin/benefits');
      return;
    } catch (error) {
      console.log(error);
      toast({
        description: 'Error al enviar el formulario',
        title: 'Ocurrió un error',
        position: 'top-right',
        status: 'error',
        duration: 2500,
      });
      return;
    }
  };

  const capitalizeFirstLetter = (str: string | undefined) => {
    if (!str || str.length === 0) {
      return '';
    }
    return str.charAt(0).toUpperCase() + str.slice(1);
  };

  const form = useFormik({
    initialValues: {
      title: '',
      description: '',
      unlimitedQuantity: false,
      availableQuantity: null,
      status: 'active',
      type: 'discount',
      pointsCost: null,
      coverFile: null,
      bannerFile: null,
      listFile: null,
    },
    validationSchema: validationSchema,
    validateOnBlur: true,
    validateOnChange: true,
    onSubmit: handleSubmit,
  });

  return {
    formikInstance: form,
    capitalizeFirstLetter,
  };
};
